import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    overflow: 'hidden'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  }
});

export const EconomicsPNGReservesDataTable = ({ economics }) => {

  // STYLES
  const classes = useStyles();
  const currency = economics?.settings?.currency || 'cad';

  const rows = useMemo(() => {
    try {
      const data = [];
      const wellType = economics?.economicVolumesGross?.oil > 0 ? 'oil' : 'gas';

      data.push({
        id: '1',
        oilGross: getVolume('oil', 'gross', wellType), 
        oilCompanyGross: getVolume('oil', 'companyGross', wellType), 
        oilCompanyNet: getVolume('oil', 'companyNet', wellType), 
        assGasGross: getVolume('assGas', 'gross', wellType), 
        assGasCompanyGross: getVolume('assGas', 'companyGross', wellType), 
        assGasCompanyNet: getVolume('assGas', 'companyNet', wellType), 
        solutionGasGross: getVolume('solutionGas', 'gross', wellType), 
        solutionGasCompanyGross: getVolume('solutionGas', 'companyGross', wellType), 
        solutionGasCompanyNet: getVolume('solutionGas', 'companyNet', wellType), 
        nglGross: getVolume('ngl', 'gross', wellType), 
        nglCompanyGross: getVolume('ngl', 'companyGross', wellType), 
        nglCompanyNet: getVolume('ngl', 'companyNet', wellType), 
        npv0: getNPV(0),
        npv5: getNPV(1),
        npv10: getNPV(2),
        npv15: getNPV(3)
      });

      return data;

      function getVolume(fluid, type, wellType) {
        switch(fluid) {
          case 'oil':
            if (type === 'gross') {
              return Number((economics?.economicVolumesGross?.oil / 1000)?.toFixed(2));
            } else if (type === 'companyGross') {
              return Number((economics?.economicVolumesNet?.oil / 1000)?.toFixed(2));
            } else if (type === 'companyNet') {
              return Number((economics?.economicVolumesCompanyNet?.oil / 1000)?.toFixed(2));
            };
            break;
          case 'solutionGas':
            if (wellType === 'oil') {
              if (type === 'gross') {
                return Number((economics?.economicVolumesGross?.salesGas)?.toFixed(2));
              } else if (type === 'companyGross') {
                return Number((economics?.economicVolumesNet?.salesGas)?.toFixed(2));
              } else if (type === 'companyNet') {
                return Number((economics?.economicVolumesCompanyNet?.salesGas)?.toFixed(2));
              };
            } else {
              return 0;
            };
            break;
          case 'assGas':
            if (wellType === 'gas') {
              if (type === 'gross') {
                return Number((economics?.economicVolumesGross?.salesGas)?.toFixed(2));
              } else if (type === 'companyGross') {
                return Number((economics?.economicVolumesNet?.salesGas)?.toFixed(2));
              } else if (type === 'companyNet') {
                return Number((economics?.economicVolumesCompanyNet?.salesGas)?.toFixed(2));
              };
            } else {
              return 0;
            };
            break;
          case 'ngl':
            if (type === 'gross') {
              return Number(((economics?.economicVolumesGross?.ngl + economics?.economicVolumesGross?.condensate) / 1000)?.toFixed(2));
            } else if (type === 'companyGross') {
              return Number(((economics?.economicVolumesNet?.ngl + economics?.economicVolumesNet?.condensate) / 1000)?.toFixed(2));
            } else if (type === 'companyNet') {
              return Number(((economics?.economicVolumesCompanyNet?.ngl + economics?.economicVolumesCompanyNet?.condensate) / 1000)?.toFixed(2));
            };
            break;
          default:
            return 0;
        };
      };

      function getNPV(index) {
        return Number((economics?.npvNet?.cashflowBTax?.[index] / 1000)?.toFixed(2)) || 0;
      };

    } catch (error) {
    };
  }, [economics]);

  const columns = useMemo(() => {
    return [
      {
        field: 'oilGross',
        headerName: 'Lt, Med, Heavy Oil, Bitumen Gross (Mbbl)',
        width: 250,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilCompanyGross',
        headerName: 'Lt, Med, Heavy Oil, Bitumen Comp. Gross (Mbbl)',
        width: 300,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilCompanyNet',
        headerName: 'Lt, Med, Heavy Oil, Bitumen Comp. Net (Mbbl)',
        width: 300,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasGross',
        headerName: 'Non-Assoc., Assoc. Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasCompanyGross',
        headerName: 'Non-Assoc., Assoc. Comp. Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'assGasCompanyNet',
        headerName: 'Non-Assoc., Assoc. Comp. Net (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasGross',
        headerName: 'Solution Gas Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasCompanyGross',
        headerName: 'Solution Gas Comp. Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'solutionGasCompanyNet',
        headerName: 'Solution Gas Comp. Net (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nglGross',
        headerName: 'NGL/Sulphur Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nglCompanyGross',
        headerName: 'NGL/Sulphur Comp. Gross (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nglCompanyNet',
        headerName: 'NGL/Sulphur Comp. Net (MMscf)',
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv0',
        headerName: `NPV 0% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv5',
        headerName: `NPV 5% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv10',
        headerName: `NPV 10% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv15',
        headerName: `NPV 15% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      }
    ];
  }, [currency]);

  try {

    if (rows) {
      return (
        <div className={classes.root}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10,25,50,100]}
            disableSelectionOnClick
            className={classes.table}
            components={{
              Toolbar: GridToolbar,
            }}
            sx={{fontSize: 12}}
            rowHeight={24}
            autoHeight={true}
          />
        </div>
      );
    } else {
      return <></>;
    };

  } catch (error) {
    return <></>
  };

};
